import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { useState } from 'react';
import { modal, ModalLayout, ModalSize, ModalTemplate } from '@ardoq/modal';
import { SchedulePolicySelector } from 'integrations/common/components/schedulePolicySelector/SchedulePolicySelector';
import { RunAsSelector } from 'integrations/common/components/runAsSelector/RunAsSelector';
import {
  IntegrationJobPayload,
  IntegrationSchedule,
  RunAs,
  SchedulingPolicy,
  APIServiceAccount,
  APICurrentUser,
  ArdoqId,
  APIOrganizationUser,
} from '@ardoq/api-types';
import { Checkbox } from '@ardoq/forms';
import { InlineLink } from '@ardoq/link';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { updateSchedule } from 'integrations/common/streams/schedules/actions';
import { trackIntegrationEvent } from 'integrations/common/tracking/actions';
import { getScheduleDirectionId } from 'integrations/common/streams/schedules/utils';
import { omit } from 'lodash';
import { isJobWithTabularMapping } from 'integrations/common/utils/scheduleApi';
import { combineLatest, map } from 'rxjs';
import { serviceAccounts$ } from 'integrations/common/streams/serviceAccounts/serviceAccounts$';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { type I18nLocale, t } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type EditScheduleModalProps = {
  onSubmit: VoidFunction;
  onCancel: VoidFunction;
  schedule: IntegrationSchedule;
  integrationId: IntegrationId;
  serviceAccounts: APIServiceAccount[];
  currentUser: APICurrentUser;
  isLoadingServiceAccounts: boolean;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  i18nLocale: I18nLocale;
  locale: Locale;
};

const getDirectionLabel = (
  i18nLocale: I18nLocale,
  type: IntegrationSchedule['jobOptions']['type']
): string => {
  if (getScheduleDirectionId(type) === 'export') {
    return t(
      i18nLocale,
      'integrations:editScheduleModal.directionExport',
      'Export'
    );
  }
  return t(
    i18nLocale,
    'integrations:editScheduleModal.directionImport',
    'Import'
  );
};

const EditScheduleModalView = ({
  onSubmit,
  onCancel,
  schedule,
  integrationId,
  serviceAccounts,
  currentUser,
  isLoadingServiceAccounts,
  orgUsersById,
  i18nLocale,
  locale,
}: EditScheduleModalProps) => {
  const [schedulingPolicy, setSchedulingPolicy] = useState<SchedulingPolicy>(
    schedule.schedulingPolicy
  );
  const [managesWorkspace, setManagesWorkspace] = useState<boolean | undefined>(
    isJobWithTabularMapping(schedule.jobOptions)
      ? schedule.jobOptions.managesWorkspace
      : undefined
  );
  const [runAs, setRunAs] = useState<RunAs>(schedule.jobOptions.runAs);

  const handleSaveChanges = () => {
    dispatchAction(
      trackIntegrationEvent({
        integrationId,
        name: 'SELECTED_SCHEDULE_INTERVAL',
        metadata: {
          source: 'overview-page-modal',
          interval: schedulingPolicy.type,
          // daily and weekly schedule will never be here since they would be migrated
          cronExpression:
            schedulingPolicy.type === 'cron'
              ? schedulingPolicy.cronExpression
              : null,
          managesWorkspace: managesWorkspace ? 'on' : 'off',
        },
      })
    );
    dispatchAction(
      updateSchedule({
        integrationId,
        schedule: {
          ...omit(schedule, 'meta'),
          schedulingPolicy,
          jobOptions: {
            ...schedule.jobOptions,
            ...(managesWorkspace !== undefined ? { managesWorkspace } : {}),
            runAs,
          },
        } as IntegrationJobPayload,
      })
    );
    onSubmit();
  };

  return (
    <ModalTemplate
      modalSize={ModalSize.S}
      headerText={t(
        i18nLocale,
        'integrations:editScheduleModal.headerText',
        'Edit options'
      )}
      primaryButtonText={t(
        i18nLocale,
        'integrations:editScheduleModal.primaryButtonText',
        'Save changes'
      )}
      secondaryButtonText={t(
        i18nLocale,
        'integrations:editScheduleModal.secondaryButtonText',
        'Close'
      )}
      onPrimaryButtonClick={handleSaveChanges}
      onSecondaryButtonClick={onCancel}
    >
      <ModalLayout>
        <SchedulePolicySelector
          title={t(
            i18nLocale,
            'integrations:editScheduleModal.intervalLabel',
            {
              direction: getDirectionLabel(
                i18nLocale,
                schedule.jobOptions.type
              ),
            },
            '{{direction}} interval'
          )}
          schedulePolicy={schedulingPolicy}
          onSchedulePolicyChange={setSchedulingPolicy}
          i18nLocale={i18nLocale}
        ></SchedulePolicySelector>
        <RunAsSelector
          value={runAs}
          onValueChange={setRunAs}
          serviceAccounts={serviceAccounts}
          currentUser={currentUser}
          isLoading={isLoadingServiceAccounts}
          orgUsersById={orgUsersById}
          i18nLocale={i18nLocale}
          locale={locale}
        />
        {managesWorkspace !== undefined && (
          <Checkbox
            onChange={() => setManagesWorkspace(prevValue => !prevValue)}
            isChecked={managesWorkspace}
          >
            <div>
              {t(
                i18nLocale,
                'integrations:editScheduleModal.disableManualDataChanges',
                'Disable manual data changes in the workspace.'
              )}
              <br />
              <span>
                {t(
                  i18nLocale,
                  'integrations:editScheduleModal.updateOnlyFromScheduledImport',
                  'Update only data from the scheduled import. Learn more about'
                )}{' '}
                <InlineLink
                  target="_blank"
                  opensInNewTabLabel={t(
                    i18nLocale,
                    'common:labels.opensInNewTab',
                    '(new tab)'
                  )}
                  href={KnowledgeBaseLink.MANAGED_WORKSPACES}
                >
                  {t(
                    i18nLocale,
                    'integrations:editScheduleModal.dataChangesLink',
                    'data changes'
                  )}
                </InlineLink>
              </span>
            </div>
          </Checkbox>
        )}
      </ModalLayout>
    </ModalTemplate>
  );
};

const viewModel$ = combineLatest([
  serviceAccounts$,
  loadedCurrentUser$,
  orgUsers$,
  i18nLocale$,
  locale$,
]).pipe(
  map(
    ([
      serviceAccountsState,
      currentUser,
      orgUsersState,
      i18nLocale,
      locale,
    ]) => ({
      serviceAccounts: serviceAccountsState.serviceAccounts,
      currentUser,
      isLoadingServiceAccounts: serviceAccountsState.isLoading,
      orgUsersById: orgUsersState.byId,
      i18nLocale,
      locale,
    })
  )
);

const EditScheduleModal = connect(EditScheduleModalView, viewModel$);

export const startEditScheduleIntervalModal = (
  integrationId: IntegrationId,
  schedule: IntegrationSchedule
) =>
  modal<boolean>(
    resolve => (
      <EditScheduleModal
        onSubmit={() => resolve(true)}
        onCancel={() => resolve(false)}
        integrationId={integrationId}
        schedule={schedule}
      />
    ),
    {
      autoFocus: true,
      shouldCloseOnEscapeKey: true,
      shouldCloseOnBackdropClick: false,
    }
  );
